/**
 * A linha do tempo do pedido de ajuste (D11 R1): uma etapa por linha, com a
 * hora e o estado, e a etapa em curso dizendo o que se espera e de quem.
 * Ícones de traço 16 px do `@tabler/icons-react`; hora em JetBrains Mono.
 */
import { Anchor, Group, Stack, Text } from '@mantine/core'
import { IconCircle, IconCircleCheck, IconCircleX, IconHourglass, IconLoader2 } from '@tabler/icons-react'
import type { PedidoDoCartao, EtapaDoPedido } from '@/api/terraformRunner'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }
const MONO = { fontFamily: '"JetBrains Mono", monospace' }

function Icone({ status, esperaPessoa = false }: {
  status: EtapaDoPedido['status']
  esperaPessoa?: boolean
}) {
  // Fix round 1 (minor): a mesma sombra de teal que o resto do cartão usa —
  // `--mantine-primary-color-filled` é o que `color="inovareTeal"` bare
  // resolve (tema tem `primaryColor: 'inovareTeal'`, `primaryShade: 7`), não
  // o `-6` fixo que este arquivo tinha copiado de outro cartão.
  if (status === 'feita') return <IconCircleCheck size={16} color="var(--mantine-primary-color-filled)" />
  // Em curso: o círculo gira quando o sistema trabalha; quando a vez é de uma
  // pessoa (Confirmar, aprovação, merge, GMUD) nada executa, e a ampulheta
  // parada diz isso. Antes o círculo ficava parado nos dois casos e parecia
  // travado (pedido #43, prd, 15/10/2026).
  if (status === 'em_curso' && esperaPessoa) {
    return <IconHourglass size={16} color="var(--mantine-primary-color-filled)" aria-label="esperando uma pessoa" />
  }
  if (status === 'em_curso') {
    return <IconLoader2 size={16} color="var(--mantine-primary-color-filled)" className="etapa-girando"
                        aria-label="em execução" />
  }
  if (status === 'terminal') return <IconCircleX size={16} color="var(--mantine-color-red-6)" />
  return <IconCircle size={16} color="var(--mantine-color-gray-5)" />
}

function prazo(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function hora(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * `mrUrl`: na etapa do MR, o link vai junto de "esperando quem" — quem precisa
 * agir vê onde agir. `desfecho` (pedido que parou): o que aconteceu, em que
 * estado ficaram o código e a AWS, e o próximo passo. No pedido #41 (prd,
 * 15/10/2026) a tela só dizia "Divergiu em: module.waf..." depois do merge.
 */
export function LinhaDoTempoDoPedido({ linha, mrUrl = null }: {
  linha: PedidoDoCartao['linha_do_tempo']
  mrUrl?: string | null
}) {
  const desfecho = linha.desfecho ?? null
  return (
    <Stack gap="xs" mb="xs">
    <Stack gap={4} component="ol" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {linha.etapas.map((e) => {
        const emCurso = linha.em_curso && e.status === 'em_curso' ? linha.em_curso : null
        return (
          <li key={e.estado}>
            <Group gap={6} wrap="nowrap" align="flex-start">
              <Icone status={e.status} esperaPessoa={emCurso?.espera_pessoa ?? false} />
              <Stack gap={0}>
                <Group gap={6} wrap="nowrap">
                  <Text size="sm" fw={e.status === 'em_curso' ? 600 : 400}
                        c={e.status === 'pendente' ? 'dimmed' : undefined} style={BARLOW}>
                    {e.rotulo}
                  </Text>
                  {/* Na etapa em curso a hora é o INÍCIO dela, e "desde" diz isso:
                      sozinha, "12:01" lia como hora em que a etapa terminou
                      (spec 2026-10-09 F1.6). */}
                  {hora(e.em) && (
                    <Text size="xs" c="dimmed" style={MONO}>
                      {e.status === 'em_curso' ? `desde ${hora(e.em)}` : hora(e.em)}
                    </Text>
                  )}
                </Group>
                {emCurso && (
                  <Text size="xs" style={BARLOW}>
                    {emCurso.frase} — esperando {emCurso.quem}
                    {emCurso.estimativa_min != null && ` · costuma levar ~${emCurso.estimativa_min} min`}
                    {emCurso.prazo_ate && (
                      <> · vence em <span style={MONO}>{prazo(emCurso.prazo_ate)}</span></>
                    )}
                    {emCurso.estado === 'mr_aberto' && mrUrl && (
                      <> · <Anchor href={mrUrl} target="_blank" rel="noreferrer" size="xs">Ver MR</Anchor></>
                    )}
                  </Text>
                )}
              </Stack>
            </Group>
          </li>
        )
      })}
    </Stack>
    {desfecho && (
      <Stack gap={2} aria-label="O que aconteceu com o pedido">
        <Text size="sm" fw={600} style={BARLOW}>{desfecho.o_que_aconteceu}</Text>
        <Text size="sm" style={BARLOW}>{desfecho.codigo_e_aws}</Text>
        <Text size="sm" style={BARLOW}>Próximo passo: {desfecho.proximo_passo}</Text>
      </Stack>
    )}
    </Stack>
  )
}
